import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ProjectDetail from "./ProjectDetail";

import { useAppSelector } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";

import {
  FieldState,
  valFileTotalSize,
} from "../../../../utils/ValidationFunctions";
import { RequestState } from "../../../../utils/RequestState";
import {
  useGetProjectAdmin,
  useUpdateProjectAdmin,
} from "../../../../utils/useProject";
import ProjectDetailInfo from "./ProjectDetailInfo";
import ProjectForm from "./ProjectForm";
import { TermType, useGetTerm } from "../../../../utils/useTerm";
import { getTermOptions } from "../../../../utils/GeneralUtils";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import {
  fieldStateToUpdateArgs,
  validFieldState,
} from "../../../mypage/user_settings/FieldStateFunctions";

type Props = {
  isSelect: boolean;
};
const ProjectDetailContainer: React.FC<Props> = ({ isSelect }) => {
  const { id } = useParams<{ id: string }>();

  const auth_token = useAppSelector(getAuthToken);

  const [fieldState, setFieldState] = useState<FieldState>();
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  const queryKey = {
    auth_token: auth_token,
    is_jikei_request: !isSelect,
    id,
  };

  const {
    items: { projects: projectData, owners },
    isLoading,
  } = useGetProjectAdmin(queryKey, auth_token ? true : false);
  const project = projectData[0];
  const owner = owners[0];

  const {
    termData: { entries: projectTypes },
  } = useGetTerm(TermType.ProjectType, undefined, -1);

  const { mutateAsync, mutateLoading } = useUpdateProjectAdmin([
    "projectAdmin",
    queryKey,
  ]);

  useEffect(() => {
    if (isLoading || !projectData) return;

    setImgItems([...projectData[0].imgs]);
    setGames([...projectData[0].game]);
  }, [isLoading]);

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  const handleUpdate = async () => {
    if (auth_token && id) {
      const update_args: { [key: string]: string | File } = {};

      if (
        !validFieldState(
          "edit",
          ["sonic_name", "sonic_type", "sonic_start_date"],
          fieldState
        )
      ) {
        setError(true);
        return;
      }

      // Check file size
      if (!valFileTotalSize(imgItems)) {
        setError(true);
        return;
      }

      // Images
      if (imgItems) {
        imgItems.forEach((v, i) => {
          if (v instanceof File) {
            update_args[`sonic_imgs${i}`] = v;
          }
        });
      }

      if (deleteImg) {
        deleteImg.forEach((v, i) => {
          update_args[`sonic_delete_imgs${i}`] = v;
        });
      }

      // MyGame
      if (games && games.length > 0) {
        const gameIds: Array<string> = [];
        games.forEach((game) => gameIds.push(game.id));
        update_args["sonic_game"] = JSON.stringify(gameIds);
      } else {
        update_args["sonic_game"] = JSON.stringify([]);
      }

      // Others
      fieldStateToUpdateArgs(update_args, fieldState);

      setError(false);
      setRequestState(RequestState.Doing);

      update_args["sonic_id"] = id;

      await mutateAsync({
        auth_token: auth_token,
        update: update_args,
        is_jikei_request: !isSelect,
      });

      setRequestState(RequestState.Success);
      setFieldState(undefined);
      setShowStatusMenu(false);
      setDeleteImg([]);
    }
  };

  const [error, setError] = useState<boolean>(false);
  const [imgItems, setImgItems] = useState<Array<File | ImageSourceSetData>>(
    []
  );
  const [deleteImg, setDeleteImg] = useState<Array<string>>([]);
  const [games, setGames] = useState<Array<SonicTermData>>([]);

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value !== undefined) {
      setRequestState(RequestState.Pending);
      setError(false);
      if (value instanceof File) {
        if (!imgItems) {
          setImgItems([value]);
          return;
        } else {
          const newList = [...imgItems, value];
          setImgItems(newList);
          return;
        }
      } else {
        f[name] = [value, true];
      }
    } else {
      if (name !== "sonic_imgs") f[name] = ["", false];
    }
    setFieldState({ ...f });
  };

  const deleteImgItem = (img: File | ImageSourceSetData, i: number) => {
    setRequestState(RequestState.Pending);
    setError(false);

    if (!(img instanceof File)) {
      const del = deleteImg;
      if (!del.includes(img.id.toString())) {
        del.push(img.id.toString());

        setDeleteImg([...del]);
      }
    }

    const newList = imgItems.filter((v, index) => index !== i);
    setImgItems(newList);
  };

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
    setRequestState(RequestState.Pending);
    setError(false);
  };

  return (
    <ProjectDetail
      isLoading={isLoading || mutateLoading}
      project={project}
      owner={owner}
      selectStatus={
        typeof fieldState?.["sonic_status"]?.[0] === "string"
          ? fieldState["sonic_status"][0]
          : ""
      }
      handleInputChange={handleInputChange}
      handleUpdate={handleUpdate}
      showStatusMenu={showStatusMenu}
      handleStatusMenu={handleStatusMenu}
      isSelect={isSelect}
      requestState={requestState}
      error={error}
    >
      {project ? (
        isSelect ? (
          <ProjectForm
            project={project}
            handleInputChange={handleInputChange}
            projectTypeOptions={getTermOptions(projectTypes)}
            games={games}
            handleGames={handleGames}
            imgs={imgItems}
            deleteImgItem={deleteImgItem}
          />
        ) : (
          <ProjectDetailInfo project={project} />
        )
      ) : (
        <></>
      )}
    </ProjectDetail>
  );
};

export default ProjectDetailContainer;
